import React, { useState, useEffect } from 'react';
import {
  Users,
  Activity,
  TrendingUp,
  UserPlus,
  Wallet,
  Settings,
  CheckCircle,
  Clock,
  XCircle,
  Shield,
  AlertTriangle,
  Edit,
  Plus,
  Lock,
  Unlock,
  Gift,
  Percent,
  Calendar,
  Award,
  ChevronDown,
  ChevronRight,
  List,
  Network,
  Layers
} from 'lucide-react';
import type { User, UserAsset, UserTransaction, ReferralNode, EventParticipation } from '../../types/user';
import voltronApiService from '../../services/voltronApiService';
import ReferralNodeMap from '../../components/ReferralNodeMap';
import ReferralPyramid, { PyramidNode } from '../../components/ReferralPyramid';

interface UserDetailPageProps {
  user: User;
  onClose: () => void;
  onUserUpdate: (user: User) => void;
}

const UserDetailPage: React.FC<UserDetailPageProps> = ({ user, onClose, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState<'info' | 'wallet' | 'transactions' | 'referrals' | 'events' | 'auth' | 'payments' | 'airdrops' | 'permissions'>('info');
  const [userAssets, setUserAssets] = useState<UserAsset[]>([]);
  const [userTransactions, setUserTransactions] = useState<UserTransaction[]>([]);
  const [referralTree, setReferralTree] = useState<ReferralNode | null>(null);
  const [eventParticipations, setEventParticipations] = useState<EventParticipation[]>([]);
  const [selectedUser, setSelectedUser] = useState<User>(user);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [referralViewMode, setReferralViewMode] = useState<'list' | 'pyramid' | 'network'>('list');
  const [referralNetwork, setReferralNetwork] = useState<{ nodes: any[], links: any[] } | null>(null);
  const [loadingNetwork, setLoadingNetwork] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);

  // 컴포넌트 마운트 시 기본 정보만 로드
  useEffect(() => {
    loadUserDetails(false);
  }, []);

  // activeTab이 변경될 때마다 필요한 데이터 로드
  useEffect(() => {
    if (activeTab === 'referrals') {
      if (!referralTree) {
        loadReferralTree();
      }
      if (referralViewMode === 'network') {
        loadReferralNetwork(showAllUsers);
      }
    }
    if ((activeTab === 'events' || activeTab === 'airdrops') && eventParticipations.length === 0) {
      loadEventParticipations();
    }
  }, [activeTab, referralViewMode]);

  // 전체 회원 보기 체크박스 변경 시 네트워크 다시 로드
  useEffect(() => {
    if (activeTab === 'referrals' && referralViewMode === 'network') {
      loadReferralNetwork(showAllUsers);
    }
  }, [showAllUsers]);

  const loadUserDetails = async (background: boolean = false) => {
    try {
      // 1) 기본 회원 상세 정보 (최우선 로드)
      const detail: any = await voltronApiService.getUserDetails(user.id);
      const dbUser = detail.user || {};
      
      // 백엔드에서 추가 정보 가져오기 (nodes, stakes, transactions, referrals)
      const nodes = detail.nodes || [];
      const stakes = detail.stakes || [];
      const transactions = detail.transactions || [];
      const referrals = detail.referrals || [];

      // selectedUser 를 DB 값으로 보정
      const mergedUser: User = {
        ...selectedUser,
        nickname: dbUser.nickname || selectedUser.nickname,
        address: dbUser.wallet_address || selectedUser.address,
        userCode: selectedUser.userCode,
        email: dbUser.email || selectedUser.email,
        phone: dbUser.phone || selectedUser.phone,
        country: dbUser.country || selectedUser.country,
        tier: (dbUser.tier || selectedUser.tier || 'bronze').toLowerCase(),
        kycStatus: dbUser.kyc_status || selectedUser.kycStatus,
        referralCode: dbUser.referral_code || selectedUser.referralCode,
        referredBy: dbUser.referred_by || selectedUser.referredBy,
        vttnBalance: parseFloat(dbUser.vttn_balance ?? selectedUser.vttnBalance ?? 0),
        vtmBalance: parseFloat(dbUser.vtm_balance ?? selectedUser.vtmBalance ?? 0),
        usdtBalance: parseFloat(dbUser.usdt_balance ?? selectedUser.usdtBalance ?? 0),
        lockedBalance: parseFloat(dbUser.locked_balance ?? selectedUser.lockedBalance ?? 0),
        bonusBalance: parseFloat(dbUser.bonus_balance ?? selectedUser.bonusBalance ?? 0),
        penaltyAmount: parseFloat(dbUser.penalty_amount ?? selectedUser.penaltyAmount ?? 0),
        totalTransactions: transactions.length || (dbUser.total_transactions ?? selectedUser.totalTransactions ?? 0),
        totalVolume: parseFloat(dbUser.total_volume ?? selectedUser.totalVolume ?? 0),
        stakingAmount: stakes.reduce((sum: number, s: any) => sum + parseFloat(s.amount || s.stake_amount || 0), 0) || parseFloat((dbUser.total_staked ?? selectedUser.stakingAmount ?? 0).toString()),
        referralCount: referrals.length || (dbUser.total_referrals ?? selectedUser.referralCount ?? 0),
        rewardsEarned: parseFloat(dbUser.total_earned ?? selectedUser.rewardsEarned ?? 0),
        boomParticipation: selectedUser.boomParticipation ?? 0,
        energyStations: selectedUser.energyStations ?? 0,
        welcomeAirdropReceived: dbUser.welcome_airdrop_received ?? selectedUser.welcomeAirdropReceived ?? false,
        welcomeEventCompleted: dbUser.welcome_event_completed ?? selectedUser.welcomeEventCompleted ?? false,
        biometricEnabled: dbUser.biometric_enabled ?? selectedUser.biometricEnabled ?? false,
        isActive: dbUser.is_active ?? selectedUser.isActive ?? true,
        lastActive: dbUser.last_login || selectedUser.lastActive,
        joinedAt: dbUser.created_at || selectedUser.joinedAt,
        notes: dbUser.admin_notes || selectedUser.notes
      };

      setSelectedUser(mergedUser);
      onUserUpdate(mergedUser);

      // 첫 화면 즉시 표시 (백그라운드 로드 제거)
      // 나머지 데이터는 탭 활성화 시 로드

      // 2) 자산 정보 (지갑앱 실제 데이터, VTTN/USDT만 표시)
      try {
        const assetsRes: any = await voltronApiService.getUserAssets(user.id);
        const balances = assetsRes.assets?.database || {};
        
        // 실제 DB 값 사용 (mergedUser의 값이 우선, 없으면 assets API 값 사용)
        const vttnBalance = (mergedUser.vttnBalance ?? 0) > 0 ? (mergedUser.vttnBalance ?? 0) : (balances.vttn ?? 0);
        const usdtBalance = (mergedUser.usdtBalance ?? 0) > 0 ? (mergedUser.usdtBalance ?? 0) : (balances.usdt ?? 0);
        
        // mergedUser 업데이트 (자산 정보 반영)
        const updatedUser = {
          ...mergedUser,
          vttnBalance: vttnBalance,
          usdtBalance: usdtBalance
        };
        setSelectedUser(updatedUser);
        onUserUpdate(updatedUser);
        
        const assets: UserAsset[] = [
          {
            token: 'VTTN',
            symbol: 'VTTN',
            balance: parseFloat(vttnBalance.toString()) || 0,
            value: parseFloat(vttnBalance.toString()) || 0,
            percentage: 0
          },
          {
            token: 'USDT',
            symbol: 'USDT',
            balance: parseFloat(usdtBalance.toString()) || 0,
            value: parseFloat(usdtBalance.toString()) || 0,
            percentage: 0
          }
        ];
        
        // 비율 계산
        const totalValue = assets.reduce((sum, a) => sum + (a.value || 0), 0) || 1;
        const assetsWithPct = assets.map((a) => ({
          ...a,
          percentage: Math.round(((a.value || 0) / totalValue) * 100)
        }));
        
        setUserAssets(assetsWithPct);
      } catch (assetsError) {
        console.error('자산 정보 로드 실패:', assetsError);
        // 실패 시 mergedUser의 값 사용
        const assets: UserAsset[] = [
          {
            token: 'VTTN',
            symbol: 'VTTN',
            balance: mergedUser.vttnBalance || 0,
            value: mergedUser.vttnBalance || 0,
            percentage: 0
          },
          {
            token: 'USDT',
            symbol: 'USDT',
            balance: mergedUser.usdtBalance || 0,
            value: mergedUser.usdtBalance || 0,
            percentage: 0
          }
        ];
        
        const totalValue = assets.reduce((sum, a) => sum + (a.value || 0), 0) || 1;
        const assetsWithPct = assets.map((a) => ({
          ...a,
          percentage: Math.round(((a.value || 0) / totalValue) * 100)
        }));
        
        setUserAssets(assetsWithPct);
      }

      // 3) 트랜잭션 히스토리 (전체 로드, 페이징 없음)
      try {
        let transactionsToUse = transactions;
        if (!transactionsToUse || transactionsToUse.length === 0) {
          // 전체 트랜잭션 로드 (페이징 없음)
          const txRes: any = await voltronApiService.getUserTransactions(user.id, { page: 1, limit: 10000 });
          transactionsToUse = txRes.transactions || txRes.data?.transactions || [];
        }
        
        const txs = transactionsToUse.map((t: any): UserTransaction => {
          const txType = t.transaction_type || t.type || 'transfer';
          const amount = parseFloat(t.amount ?? 0);
          const fromAddress = t.from_address || t.fromAddress || '';
          const toAddress = t.to_address || t.toAddress || t.user_address || '';
          const userAddress = mergedUser.address || '';
          
          // 입금/출금 구분
          let displayType = txType;
          let isDeposit = false;
          let isWithdrawal = false;
          
          if (txType === 'airdrop' || txType === 'bonus' || txType === 'referral_bonus' || txType === 'reward' || txType === 'staking_reward') {
            isDeposit = true;
            displayType = '입금';
          } else if (txType === 'transfer' || txType === 'withdrawal' || txType === 'send') {
            if (toAddress.toLowerCase() === userAddress.toLowerCase()) {
              isDeposit = true;
              displayType = '입금';
            } else if (fromAddress.toLowerCase() === userAddress.toLowerCase()) {
              isWithdrawal = true;
              displayType = '출금';
            }
          } else if (txType === 'deposit') {
            isDeposit = true;
            displayType = '입금';
          } else if (txType === 'withdrawal') {
            isWithdrawal = true;
            displayType = '출금';
          }
          
          return {
            id: t.id?.toString() || t.tx_hash || `${t.created_at}`,
            type: displayType as string,
            amount: amount,
            token: t.token_type || t.token || 'VTTN',
            status: (t.status || 'completed') as 'pending' | 'completed' | 'failed',
            timestamp: t.created_at || t.timestamp,
            hash: t.tx_hash || t.hash,
            isDeposit,
            isWithdrawal
          };
        });
        setUserTransactions(txs);
      } catch (txError) {
        console.error('거래 내역 로드 실패:', txError);
        setUserTransactions([]);
      }
    } catch (error) {
      console.error('사용자 상세 정보 로드 실패:', error);
    }
  };

  const loadEventParticipations = async () => {
    try {
      const res: any = await voltronApiService.getUserEvents(user.id);
      console.log('이벤트 참여 내역 API 응답:', res);
      
      if (res && res.success && Array.isArray(res.events)) {
        const events: EventParticipation[] = res.events.map((e: any) => ({
          id: e.id?.toString() || `${e.eventType}-${e.participatedAt}`,
          eventName: e.eventName || e.event_name || e.type || '이벤트',
          eventType: e.eventType || e.type || 'airdrop',
          participatedAt: e.participatedAt || e.participated_at || e.created_at || new Date().toISOString(),
          status: e.status || 'completed',
          reward: parseFloat(e.reward || e.amount || 0),
          requirements: e.requirements || e.description || '',
          rank: e.rank || e.ranking || undefined,
          isWinner: e.isWinner || e.is_winner || e.rank !== undefined && e.rank <= 10 || false,
          prizeAmount: e.prizeAmount || e.prize_amount || (e.rank !== undefined && e.rank <= 10 ? parseFloat(e.reward || e.amount || 0) : 0)
        }));
        setEventParticipations(events);
      } else if (res && Array.isArray(res.events)) {
        // success 필드가 없어도 events 배열이 있으면 사용
        const events: EventParticipation[] = res.events.map((e: any) => ({
          id: e.id?.toString() || `${e.eventType}-${e.participatedAt}`,
          eventName: e.eventName || e.event_name || e.type || '이벤트',
          eventType: e.eventType || e.type || 'airdrop',
          participatedAt: e.participatedAt || e.participated_at || e.created_at || new Date().toISOString(),
          status: e.status || 'completed',
          reward: parseFloat(e.reward || e.amount || 0),
          requirements: e.requirements || e.description || '',
          rank: e.rank || e.ranking || undefined,
          isWinner: e.isWinner || e.is_winner || e.rank !== undefined && e.rank <= 10 || false,
          prizeAmount: e.prizeAmount || e.prize_amount || (e.rank !== undefined && e.rank <= 10 ? parseFloat(e.reward || e.amount || 0) : 0)
        }));
        setEventParticipations(events);
      } else {
        setEventParticipations([]);
      }
    } catch (error) {
      console.warn('이벤트/리워드 참여 내역 API 미구현 또는 오류:', error);
      setEventParticipations([]);
    }
  };

  // 레퍼럴 트리 지연 로드 - 1차만 먼저 로드하고, 클릭 시 하위 레벨 로드
  const loadReferralTreeLevel = async (userId: string, level: number = 1): Promise<ReferralNode[]> => {
    try {
      const res: any = await voltronApiService.getUserReferralTree(userId);
      if (!res || !res.success || !res.referralTree || !Array.isArray(res.referralTree)) {
        return [];
      }

      const levelRefs = res.referralTree;
      if (levelRefs.length === 0) {
        return [];
      }

      // 백엔드가 이미 반환하는 데이터 사용 (getUserDetails 호출 최소화)
      // 백엔드는 1차와 2차를 모두 반환하므로 2차도 함께 처리
      const children: ReferralNode[] = levelRefs.map((ref: any) => {
        // 2차 레퍼럴도 백엔드에서 제공하므로 변환
        const level2Children: ReferralNode[] = (ref.children || []).map((l2: any) => ({
          user: {
            id: l2.id?.toString() || `ref-l2-${l2.wallet_address}`,
            userCode: `USR${(l2.id ?? 0).toString().padStart(4, '0')}`,
            nickname: l2.nickname || 'N/A',
            address: l2.wallet_address || '',
            joinedAt: l2.created_at || '',
            lastActive: '',
            status: 'active',
            tier: 'bronze',
            totalTransactions: 0,
            totalVolume: parseFloat(l2.vttn_balance || 0),
            stakingAmount: 0,
            referralCode: undefined,
            referredBy: undefined,
            referralCount: 0,
            boomParticipation: 0,
            energyStations: 0,
            email: undefined,
            phone: undefined,
            kycStatus: undefined,
            country: undefined,
            ipAddress: '',
            rewardsEarned: 0,
            commissionRate: 0,
            bonusBalance: 0,
            penaltyAmount: 0,
            vttnBalance: parseFloat(l2.vttn_balance || 0),
            vtmBalance: 0,
            usdtBalance: 0,
            lockedBalance: 0,
            notes: undefined
          },
          level: level + 1,
          totalReferred: 0,
          totalVolume: parseFloat(l2.vttn_balance || 0),
          children: [] // 3차 이상은 지연 로드
        }));

        return {
          user: {
            id: ref.id?.toString() || `ref-${ref.wallet_address}`,
            userCode: `USR${(ref.id ?? 0).toString().padStart(4, '0')}`,
            nickname: ref.nickname || 'N/A',
            address: ref.wallet_address || ref.walletAddress || '',
            joinedAt: ref.created_at || '',
            lastActive: '',
            status: 'active',
            tier: (ref.tier || 'bronze').toLowerCase(),
            totalTransactions: 0,
            totalVolume: parseFloat(ref.vttn_balance || ref.total_volume || 0),
            stakingAmount: 0,
            referralCode: ref.referral_code || undefined,
            referredBy: undefined,
            referralCount: level2Children.length, // 2차 추천인 수
            boomParticipation: 0,
            energyStations: 0,
            email: undefined,
            phone: undefined,
            kycStatus: undefined,
            country: undefined,
            ipAddress: '',
            rewardsEarned: 0,
            commissionRate: 0,
            bonusBalance: 0,
            penaltyAmount: 0,
            vttnBalance: parseFloat(ref.vttn_balance || 0),
            vtmBalance: 0,
            usdtBalance: 0,
            lockedBalance: 0,
            notes: undefined
          },
          level: level,
          totalReferred: level2Children.length, // 2차 추천인 수
          totalVolume: parseFloat(ref.vttn_balance || ref.total_volume || 0),
          children: level2Children // 백엔드가 제공하는 2차 데이터 사용
        };
      });

      return children;
    } catch (error) {
      console.error(`레벨 ${level} 레퍼럴 트리 로드 실패:`, error);
      return [];
    }
  };

  // 특정 노드의 하위 레벨 로드 (클릭 시 호출)
  const loadChildNodes = async (node: ReferralNode): Promise<void> => {
    if (node.children && node.children.length > 0) {
      // 이미 로드됨
      return;
    }

    try {
      const childNodes = await loadReferralTreeLevel(node.user.id, node.level + 1);
      
      // 노드 업데이트
      const updatedNode = {
        ...node,
        children: childNodes,
        totalReferred: childNodes.length
      };

      // referralTree 상태 업데이트
      if (referralTree) {
        const updateNodeInTree = (nodes: ReferralNode[]): ReferralNode[] => {
          return nodes.map(n => {
            if (n.user.id === node.user.id) {
              return updatedNode;
            }
            return {
              ...n,
              children: updateNodeInTree(n.children || [])
            };
          });
        };

        setReferralTree({
          ...referralTree,
          children: updateNodeInTree(referralTree.children || [])
        });
      }
    } catch (error) {
      console.error('하위 레벨 로드 실패:', error);
    }
  };

  const loadReferralTree = async () => {
    try {
      // 즉시 빈 트리 표시 (로딩 상태)
      setReferralTree({
        user: selectedUser,
        level: 0,
        totalReferred: 0,
        totalVolume: 0,
        children: []
      });
      
      // 1차 레벨만 먼저 로드 (빠른 표시)
      const children = await loadReferralTreeLevel(user.id, 1);
      
      // 총 추천인 수와 총 거래량 계산 (1차만)
      const totalReferred = children.length;
      const totalVolume = children.reduce((sum, node) => sum + (node.totalVolume || 0), 0);
      
      setReferralTree({
        user: selectedUser,
        level: 0,
        totalReferred,
        totalVolume,
        children
      });
    } catch (error) {
      console.error('레퍼럴 트리 로드 실패:', error);
      setReferralTree({
        user: selectedUser,
        level: 0,
        totalReferred: 0,
        totalVolume: 0,
        children: []
      });
    }
  };

  const loadReferralNetwork = async (showAll: boolean = false) => {
    try {
      setLoadingNetwork(true);
      const res: any = await voltronApiService.getReferralNetwork({
        userId: showAll ? undefined : user.id,
        maxDepth: 10,
        limit: showAll ? 5000 : 2000
      });

      if (res && res.success) {
        setReferralNetwork({
          nodes: res.nodes || [],
          links: res.links || []
        });
      }
    } catch (error) {
      console.error('레퍼럴 네트워크 로드 실패:', error);
      setReferralNetwork(null);
    } finally {
      setLoadingNetwork(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'suspended': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'banned': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'silver': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'gold': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'platinum': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-3 h-3" />;
      case 'suspended': return <Clock className="w-3 h-3" />;
      case 'banned': return <XCircle className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '방금 전';
    if (diffInHours < 24) return `${diffInHours}시간 전`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}일 전`;
    return formatDate(dateString);
  };

  // 레퍼럴 트리 노드 컴포넌트 (재귀적 렌더링, 지연 로드)
  const renderReferralTreeNode = (
    node: ReferralNode,
    level: number,
    expandedNodes: Set<string>,
    setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>,
    loadingNodes: Set<string>,
    setLoadingNodes: React.Dispatch<React.SetStateAction<Set<string>>>,
    onUserClick: (user: User) => void
  ) => {
    const nodeId = node.user.id;
    const isExpanded = expandedNodes.has(nodeId);
    const hasChildren = (node.children && node.children.length > 0) || (node.totalReferred > 0);
    const isLoading = loadingNodes.has(nodeId);

    const toggleExpand = async () => {
      const newSet = new Set(expandedNodes);
      if (isExpanded) {
        newSet.delete(nodeId);
        setExpandedNodes(newSet);
      } else {
        newSet.add(nodeId);
        setExpandedNodes(newSet);
        
        // 하위 레벨이 아직 로드되지 않았으면 로드
        if (!node.children || node.children.length === 0) {
          const loadingSet = new Set(loadingNodes);
          loadingSet.add(nodeId);
          setLoadingNodes(loadingSet);
          
          await loadChildNodes(node);
          
          const loadingSetAfter = new Set(loadingNodes);
          loadingSetAfter.delete(nodeId);
          setLoadingNodes(loadingSetAfter);
        }
      }
    };

    return (
      <div className="border-l-2 border-gray-200 pl-4">
        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
          {hasChildren ? (
            <button
              onClick={toggleExpand}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          ) : (
            <div className="w-6" />
          )}
          <div className="p-2 bg-gray-100 rounded-lg">
            <Users className="w-4 h-4 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              onClick={() => onUserClick(node.user)}
              className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left truncate"
            >
              {node.user.nickname}
            </button>
            <p className="text-xs text-gray-600 font-mono">{node.user.userCode}</p>
            <p className="text-xs text-gray-500">
              레벨 {level} | 추천: {node.totalReferred || 0}명 | 거래량: {(node.totalVolume || 0) > 0 ? `${((node.totalVolume || 0) / 1000).toFixed(0)}K` : '0K'} VTTN
              {node.user.referralCode && ` | 코드: ${node.user.referralCode}`}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(node.user.status)}`}>
              {node.user.status}
            </span>
            <p className="text-xs text-gray-500 mt-1">{formatDate(node.user.joinedAt)}</p>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="ml-8 mt-2 space-y-2">
            {isLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2"></div>
                <p className="text-xs text-gray-500">로딩 중...</p>
              </div>
            ) : node.children && node.children.length > 0 ? (
              node.children.map((child, index) => 
                renderReferralTreeNode(child, level + 1, expandedNodes, setExpandedNodes, loadingNodes, setLoadingNodes, onUserClick)
              )
            ) : (
              <div className="text-center py-4">
                <p className="text-xs text-gray-500">추천받은 사용자가 없습니다.</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleStatusChange = (newStatus: 'active' | 'suspended' | 'banned') => {
    const updatedUser = { ...selectedUser, status: newStatus };
    setSelectedUser(updatedUser);
    onUserUpdate(updatedUser);
    alert(`사용자 상태가 ${newStatus}로 변경되었습니다.`);
  };

  const handleUserManagement = (action: 'adjustCommission' | 'addBonus' | 'addPenalty', value: number) => {
    let updatedUser = { ...selectedUser };
    
    switch (action) {
      case 'adjustCommission':
        updatedUser.commissionRate = value;
        break;
      case 'addBonus':
        updatedUser.bonusBalance += value;
        break;
      case 'addPenalty':
        updatedUser.penaltyAmount += value;
        break;
    }
    
    setSelectedUser(updatedUser);
    onUserUpdate(updatedUser);
    alert('사용자 정보가 성공적으로 업데이트되었습니다.');
  };

  // 총 자산 계산
  const totalAssetValue = userAssets.reduce((sum, asset) => sum + asset.value, 0);
  
  // 에어드롭 총 수령액 계산
  const totalAirdropReceived = eventParticipations
    .filter(event => event.eventType === 'airdrop' && event.status === 'completed')
    .reduce((sum, event) => sum + event.reward, 0);

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onClose}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>이전으로</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{selectedUser.nickname}</h1>
                <p className="text-gray-600 font-mono">{selectedUser.userCode}</p>
                <p className="text-sm text-gray-500">지갑: {selectedUser.address.slice(0, 10)}...{selectedUser.address.slice(-8)}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedUser.status)}`}>
              {getStatusIcon(selectedUser.status)}
              <span className="ml-1 capitalize">{selectedUser.status}</span>
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(selectedUser.tier)}`}>
              {selectedUser.tier.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* 상단 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* 총 자산 (실제 지갑앱 자산 데이터 기준, VTTN/USDT 합산) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Wallet className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">총 자산</p>
              <p className="text-2xl font-bold text-gray-900">${totalAssetValue.toLocaleString()}</p>
              <p className="text-xs text-gray-500">스테이킹: {selectedUser.stakingAmount.toLocaleString()} VTTN</p>
            </div>
          </div>
        </div>
        
        {/* 에어드롭 수령액 (이벤트 탭 데이터 기반, API 미연동 시 0) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Gift className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">에어드롭</p>
              <p className="text-2xl font-bold text-gray-900">{totalAirdropReceived.toLocaleString()}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
          </div>
        </div>
        
        {/* 수수료율 / 보너스 (지갑앱 DB 기준) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Percent className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">수수료율</p>
              <p className="text-2xl font-bold text-gray-900">{(selectedUser.commissionRate * 100).toFixed(2)}%</p>
              <p className="text-xs text-gray-500">보너스: {selectedUser.bonusBalance.toLocaleString()} VTTN</p>
            </div>
          </div>
        </div>
        
        {/* 추천인 수 / 거래량 (지갑앱 DB 기준) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <UserPlus className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">추천인 수</p>
              <p className="text-2xl font-bold text-gray-900">{selectedUser.referralCount}</p>
              <p className="text-xs text-gray-500">총 거래량: {selectedUser.totalVolume > 0 ? `${(selectedUser.totalVolume / 1000).toFixed(0)}K` : '0K'} VTTN</p>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'info', label: '기본 정보', icon: Users },
              { id: 'wallet', label: '지갑 정보', icon: Wallet },
              { id: 'transactions', label: '거래 내역', icon: Activity },
              { id: 'referrals', label: '레퍼럴 관리', icon: UserPlus },
              { id: 'events', label: '이벤트 참여', icon: Calendar },
              { id: 'auth', label: '인증 관리', icon: Shield },
              { id: 'payments', label: '결제 내역', icon: TrendingUp },
              { id: 'airdrops', label: '에어드롭', icon: Gift }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        
        {/* 탭 콘텐츠 */}
        <div className="p-6">
          {/* 기본 정보 탭 */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Users className="w-5 h-5" />
                      <span>사용자 정보</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">사용자 코드</span>
                        <span className="text-sm font-mono font-semibold text-gray-900">{selectedUser.userCode}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">닉네임</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedUser.nickname}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">지갑 주소</span>
                        <span className="text-xs font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">{selectedUser.address}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">가입일</span>
                        <span className="text-sm text-gray-900">{formatDate(selectedUser.joinedAt)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">최근 활동</span>
                        <span className="text-sm text-gray-900">{formatTimeAgo(selectedUser.lastActive)}</span>
                      </div>
                      {selectedUser.email && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">이메일</span>
                          <span className="text-sm text-gray-900">{selectedUser.email}</span>
                        </div>
                      )}
                      {selectedUser.phone && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">휴대폰번호</span>
                          <span className="text-sm text-gray-900">{selectedUser.phone}</span>
                        </div>
                      )}
                      {selectedUser.country && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm font-medium text-gray-600">국가</span>
                          <span className="text-sm text-gray-900">{selectedUser.country}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <TrendingUp className="w-5 h-5" />
                      <span>활동 통계</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">총 거래 횟수</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedUser.totalTransactions.toLocaleString()}회</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">총 거래량</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedUser.totalVolume.toLocaleString()} VTTN</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">리워드 획득</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedUser.rewardsEarned.toLocaleString()} VTTN</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">BOOM 참여</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedUser.boomParticipation}회</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">에너지 스테이션</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedUser.energyStations}개</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">KYC 상태</span>
                        <span className={`text-sm font-semibold ${
                          selectedUser.kycStatus === 'verified' ? 'text-green-600' :
                          selectedUser.kycStatus === 'pending' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {selectedUser.kycStatus || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* 보유 자산 */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Wallet className="w-5 h-5" />
                      <span>보유 자산</span>
                    </h3>
                    <div className="space-y-3">
                      {userAssets.map((asset, index) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0">
                          <div>
                            <span className="text-sm font-medium text-gray-900">{asset.symbol}</span>
                            <span className="text-xs text-gray-500 ml-2">{asset.percentage}%</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{asset.balance.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">${asset.value.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 관리자 메모 */}
              {selectedUser.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>관리자 메모</span>
                  </h3>
                  <p className="text-sm text-yellow-800">{selectedUser.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* 지갑 정보 탭 (실제 자산, VTTN/USDT만 표시, VTM 제외) */}
          {activeTab === 'wallet' && (
            <div className="space-y-6">
              {/* 지갑 잔고 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">VTTN 잔고</p>
                      <p className="text-2xl font-bold">{(selectedUser.vttnBalance || 0).toLocaleString()}</p>
                      <p className="text-blue-100 text-xs">≈ ${((selectedUser.vttnBalance || 0) * 0.5).toLocaleString()}</p>
                    </div>
                    <Wallet className="w-8 h-8 text-blue-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-100 text-sm">USDT 잔고</p>
                      <p className="text-2xl font-bold">{(selectedUser.usdtBalance || 0).toLocaleString()}</p>
                      <p className="text-yellow-100 text-xs">≈ ${(selectedUser.usdtBalance || 0).toLocaleString()}</p>
                    </div>
                    <Wallet className="w-8 h-8 text-yellow-200" />
                  </div>
                </div>
              </div>

              {/* 지갑 상세 정보 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Wallet className="w-5 h-5" />
                    <span>지갑 주소</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">메인 지갑 주소</label>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono">
                          {selectedUser.address}
                        </code>
                        <button className="px-3 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">
                          복사
                        </button>
                      </div>
                    </div>
                    {selectedUser.walletAddress && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">보조 지갑 주소</label>
                        <div className="flex items-center space-x-2">
                          <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono">
                            {selectedUser.walletAddress}
                          </code>
                          <button className="px-3 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">
                            복사
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span>보안 정보</span>
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">생체 인증</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedUser.biometricEnabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedUser.biometricEnabled ? '활성화' : '비활성화'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">지갑 잠금</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        안전
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">백업 상태</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        완료
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 스테이킹 정보 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Lock className="w-5 h-5" />
                  <span>스테이킹 정보</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-purple-600 font-medium">스테이킹 중</p>
                    <p className="text-xl font-bold text-purple-900">{selectedUser.stakingAmount.toLocaleString()} VTTN</p>
                    <p className="text-xs text-purple-600">연 12% APY</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-orange-600 font-medium">예상 보상</p>
                    <p className="text-xl font-bold text-orange-900">{(selectedUser.stakingAmount * 0.12 / 365).toFixed(2)} VTTN</p>
                    <p className="text-xs text-orange-600">일일 보상</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">누적 보상</p>
                    <p className="text-xl font-bold text-green-900">{selectedUser.rewardsEarned.toLocaleString()} VTTN</p>
                    <p className="text-xs text-green-600">총 수령액</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 거래 내역 탭 */}
          {activeTab === 'transactions' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <Activity className="w-4 h-4" />
                    <span>최근 거래 내역</span>
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Hash</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Token</th>
                        <th>Status</th>
                        <th>Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userTransactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-gray-50">
                          <td>
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <Activity className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                {tx.hash ? (
                                  <button
                                    onClick={() => {
                                      // 트랜잭션 상세 페이지로 이동
                                      window.open(`/tx/${tx.hash}`, '_blank');
                                    }}
                                    className="font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    {tx.hash.slice(0, 10)}...{tx.hash.slice(-8)}
                                  </button>
                                ) : (
                                  <span className="text-gray-500">N/A</span>
                                )}
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="text-sm text-gray-500 font-mono">
                                    {tx.hash ? `${tx.hash.substring(0, 10)}...${tx.hash.slice(-8)}` : 'No hash'}
                                  </span>
                                  {tx.hash && (
                                    <button
                                      onClick={() => navigator.clipboard.writeText(tx.hash!)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${
                              tx.isDeposit || tx.type === '입금' || tx.type === 'deposit' ? 'bg-green-100 text-green-800' :
                              tx.isWithdrawal || tx.type === '출금' || tx.type === 'withdrawal' ? 'bg-red-100 text-red-800' :
                              tx.type === 'trade' ? 'bg-blue-100 text-blue-800' :
                              tx.type === 'staking' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {tx.type}
                            </span>
                          </td>
                          <td>
                            <span className={`text-sm font-medium ${
                              tx.isDeposit || tx.type === '입금' || tx.type === 'deposit' ? 'text-green-600' :
                              tx.isWithdrawal || tx.type === '출금' || tx.type === 'withdrawal' ? 'text-red-600' :
                              'text-gray-600'
                            }`}>
                              {(tx.isDeposit || tx.type === '입금' || tx.type === 'deposit' ? '+' : (tx.isWithdrawal || tx.type === '출금' || tx.type === 'withdrawal' ? '-' : ''))}
                              {tx.amount.toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <span className="font-medium">{tx.token}</span>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              {tx.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
                              {tx.status === 'pending' && <Clock className="w-4 h-4 text-yellow-500" />}
                              {tx.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                              <span className={`text-sm font-medium ${
                                tx.status === 'completed' ? 'text-green-600' :
                                tx.status === 'pending' ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {tx.status}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="text-sm text-gray-600">{formatTimeAgo(tx.timestamp)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 레퍼럴 트리 탭 */}
          {activeTab === 'referrals' && (
            <div className="space-y-6">
              {/* 레퍼럴 통계 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">총 추천인</p>
                      <p className="text-3xl font-bold text-blue-900">{selectedUser.referralCount}</p>
                    </div>
                    <UserPlus className="w-10 h-10 text-blue-600" />
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">추천 코드</p>
                      <p className="text-xl font-bold text-green-900 font-mono">{selectedUser.referralCode || 'N/A'}</p>
                    </div>
                    <Shield className="w-10 h-10 text-green-600" />
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">추천인</p>
                      <p className="text-xl font-bold text-purple-900">{selectedUser.referredBy || 'N/A'}</p>
                    </div>
                    <Users className="w-10 h-10 text-purple-600" />
                  </div>
                </div>
              </div>
              
              {/* 뷰 전환 버튼 및 전체 회원 보기 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setReferralViewMode('list')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
                        referralViewMode === 'list'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <List className="w-4 h-4" />
                      <span>리스트</span>
                    </button>
                    <button
                      onClick={() => {
                        setReferralViewMode('pyramid');
                        if (!referralTree) loadReferralTree();
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
                        referralViewMode === 'pyramid'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Layers className="w-4 h-4" />
                      <span>피라미드</span>
                    </button>
                    <button
                      onClick={() => {
                        setReferralViewMode('network');
                        if (!referralNetwork) loadReferralNetwork(showAllUsers);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
                        referralViewMode === 'network'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Network className="w-4 h-4" />
                      <span>노드맵</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showAllUsers}
                        onChange={(e) => {
                          setShowAllUsers(e.target.checked);
                          if (referralViewMode === 'network') {
                            loadReferralNetwork(e.target.checked);
                          }
                        }}
                        className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-700">전체 회원 보기</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* 레퍼럴 트리 */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <UserPlus className="w-5 h-5" />
                    <span>레퍼럴 트리</span>
                    {referralViewMode === 'network' && referralNetwork && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({referralNetwork.nodes.length}개 노드, {referralNetwork.links.length}개 연결)
                      </span>
                    )}
                  </h3>
                </div>
                <div className="p-6">
                  {/* 노드맵 뷰 */}
                  {referralViewMode === 'network' && (
                    <>
                      {loadingNetwork ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
                          <p className="text-gray-500">네트워크 로딩 중...</p>
                        </div>
                      ) : referralNetwork ? (
                        <div className="h-[800px]">
                          <ReferralNodeMap
                            nodes={referralNetwork.nodes}
                            links={referralNetwork.links}
                            selectedUserId={user.id}
                            onNodeClick={(node) => {
                              // 노드 클릭 시 해당 사용자 상세 정보 표시
                              console.log('Node clicked:', node);
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Network className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500">네트워크 데이터를 로드할 수 없습니다.</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* 피라미드 뷰 */}
                  {referralViewMode === 'pyramid' && referralTree && (() => {
                    // 모든 레벨의 children을 재귀적으로 수집
                    const collectAllChildren = (node: ReferralNode): PyramidNode[] => {
                      const result: PyramidNode[] = [];
                      if (node.children && node.children.length > 0) {
                        node.children.forEach(child => {
                          result.push({
                            id: child.user.id,
                            referralCode: child.user.referralCode || '',
                            nickname: child.user.nickname,
                            walletAddress: child.user.address,
                            referredBy: child.user.referredBy,
                            vttnBalance: child.user.vttnBalance || 0,
                            totalReferrals: child.totalReferred,
                            totalVolume: child.totalVolume,
                            createdAt: child.user.joinedAt,
                            tier: child.user.tier,
                            isActive: child.user.isActive !== false
                          });
                          // 재귀적으로 하위 레벨도 수집
                          result.push(...collectAllChildren(child));
                        });
                      }
                      return result;
                    };
                    
                    const allChildren = collectAllChildren(referralTree);
                    
                    return (
                      <ReferralPyramid
                        rootUser={{
                          id: referralTree.user.id,
                          referralCode: referralTree.user.referralCode || '',
                          nickname: referralTree.user.nickname,
                          walletAddress: referralTree.user.address,
                          referredBy: referralTree.user.referredBy,
                          vttnBalance: referralTree.user.vttnBalance || 0,
                          totalReferrals: referralTree.totalReferred,
                          totalVolume: referralTree.totalVolume,
                          createdAt: referralTree.user.joinedAt,
                          tier: referralTree.user.tier,
                          isActive: referralTree.user.isActive !== false
                        }}
                        children={allChildren}
                        onNodeClick={(node) => {
                          console.log('Pyramid node clicked:', node);
                        }}
                      />
                    );
                  })()}

                  {/* 리스트 뷰 */}
                  {referralViewMode === 'list' && (
                    referralTree ? (
                      <div className="space-y-4">
                        {/* 루트 사용자 */}
                        <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                          <div className="p-3 bg-blue-100 rounded-lg">
                            <Users className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-blue-900">{referralTree.user.nickname}</h4>
                            <p className="text-sm text-blue-700 font-mono">{referralTree.user.userCode}</p>
                            <p className="text-xs text-blue-600">총 추천: {referralTree.totalReferred}명 | 총 거래량: {referralTree.totalVolume > 0 ? `${(referralTree.totalVolume / 1000).toFixed(0)}K` : '0K'} VTTN</p>
                          </div>
                        </div>
                        
                        {/* 하위 레퍼럴 - 재귀적으로 모든 레벨 표시 */}
                        {referralTree.children && referralTree.children.length > 0 ? (
                          <div className="ml-4 space-y-2">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">추천받은 사용자들 ({referralTree.children.length}명)</h5>
                            {referralTree.children.map((child: ReferralNode, index: number) => 
                              renderReferralTreeNode(child, 1, expandedNodes, setExpandedNodes, loadingNodes, setLoadingNodes, (user: User) => onUserUpdate(user))
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500">아직 추천받은 사용자가 없습니다.</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
                        <p className="text-gray-500">레퍼럴 트리를 로딩 중...</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 권한 수정 탭 */}
          {activeTab === 'permissions' && (
            <div className="space-y-6">
              {/* 빠른 액션 */}
              <div className="bg-gradient-to-r from-orange-50 to-blue-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>빠른 액션</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleStatusChange(selectedUser.status === 'active' ? 'suspended' : 'active')}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedUser.status === 'active'
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {selectedUser.status === 'active' ? <Lock className="w-4 h-4 mr-1" /> : <Unlock className="w-4 h-4 mr-1" />}
                    {selectedUser.status === 'active' ? '계정 정지' : '계정 활성화'}
                  </button>
                  <button
                    onClick={() => {
                      const bonus = window.prompt('지급할 보너스 금액을 입력하세요:', '100');
                      if (bonus && !isNaN(parseFloat(bonus))) {
                        handleUserManagement('addBonus', parseFloat(bonus));
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    보너스 지급
                  </button>
                  <button
                    onClick={() => handleStatusChange('banned')}
                    className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    계정 차단
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 수수료 및 티어 관리 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Settings className="w-4 h-4" />
                    <span>수수료 및 티어</span>
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        현재 수수료율: {(selectedUser.commissionRate * 100).toFixed(2)}%
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="새 수수료율 (%)"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          id="commissionRate"
                        />
                        <button
                          onClick={() => {
                            const input = document.getElementById('commissionRate') as HTMLInputElement;
                            const value = parseFloat(input.value) / 100;
                            if (value >= 0 && value <= 1) {
                              handleUserManagement('adjustCommission', value);
                              input.value = '';
                            }
                          }}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          적용
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        현재 티어: <span className={`px-2 py-1 rounded text-xs ${getTierColor(selectedUser.tier)}`}>
                          {selectedUser.tier.toUpperCase()}
                        </span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {['bronze', 'silver', 'gold', 'platinum'].map(tier => (
                          <button
                            key={tier}
                            onClick={() => {
                              const updatedUser = { ...selectedUser, tier: tier as any };
                              setSelectedUser(updatedUser);
                              onUserUpdate(updatedUser);
                              alert(`티어가 ${tier}로 변경되었습니다.`);
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              selectedUser.tier === tier
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {tier.charAt(0).toUpperCase() + tier.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 보너스 및 차감 관리 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>보너스 및 차감</span>
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        현재 보너스: {selectedUser.bonusBalance.toLocaleString()} VTTN
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          placeholder="보너스 수량"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          id="bonusAmount"
                        />
                        <button
                          onClick={() => {
                            const input = document.getElementById('bonusAmount') as HTMLInputElement;
                            const value = parseFloat(input.value);
                            if (value > 0) {
                              handleUserManagement('addBonus', value);
                              input.value = '';
                            }
                          }}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          지급
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        현재 차감: {selectedUser.penaltyAmount.toLocaleString()} VTTN
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          placeholder="차감 수량"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          id="penaltyAmount"
                        />
                        <button
                          onClick={() => {
                            const input = document.getElementById('penaltyAmount') as HTMLInputElement;
                            const value = parseFloat(input.value);
                            if (value > 0) {
                              handleUserManagement('addPenalty', value);
                              input.value = '';
                            }
                          }}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          차감
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 관리자 메모 편집 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Edit className="w-4 h-4" />
                  <span>관리자 메모</span>
                </h4>
                <div className="space-y-3">
                  <textarea
                    value={selectedUser.notes || ''}
                    onChange={(e) => {
                      const updatedUser = { ...selectedUser, notes: e.target.value };
                      setSelectedUser(updatedUser);
                      onUserUpdate(updatedUser);
                    }}
                    placeholder="사용자에 대한 관리자 메모를 입력하세요..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      마지막 수정: {new Date().toLocaleString('ko-KR')}
                    </span>
                    <button
                      onClick={() => {
                        alert('메모가 저장되었습니다.');
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    >
                      저장
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 이벤트 참여 탭 */}
          {activeTab === 'events' && (
            <div className="space-y-6">
              {/* 이벤트 참여 통계 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">BOOM 참여</p>
                      <p className="text-2xl font-bold text-blue-900">{selectedUser.boomParticipation}</p>
                    </div>
                    <Award className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">에어드롭</p>
                      <p className="text-2xl font-bold text-green-900">{eventParticipations.filter(e => e.eventType === 'airdrop').length}</p>
                    </div>
                    <Gift className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">스테이킹</p>
                      <p className="text-2xl font-bold text-purple-900">{eventParticipations.filter(e => e.eventType === 'staking').length}</p>
                    </div>
                    <Wallet className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600">총 리워드</p>
                      <p className="text-2xl font-bold text-orange-900">{eventParticipations.reduce((sum, e) => sum + e.reward, 0).toLocaleString()}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-orange-600" />
                  </div>
                </div>
              </div>
              
              {/* 이벤트 참여 내역 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>이벤트 참여 내역</span>
                  </h4>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {eventParticipations.map((event) => (
                      <div key={event.id} className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                        (event as any).isWinner ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${
                              event.eventType === 'boom' ? 'bg-blue-100' :
                              event.eventType === 'airdrop' ? 'bg-green-100' :
                              event.eventType === 'staking' ? 'bg-purple-100' :
                              'bg-orange-100'
                            }`}>
                              {event.eventType === 'boom' && <Award className="w-5 h-5 text-blue-600" />}
                              {event.eventType === 'airdrop' && <Gift className="w-5 h-5 text-green-600" />}
                              {event.eventType === 'staking' && <Wallet className="w-5 h-5 text-purple-600" />}
                              {event.eventType === 'trading' && <TrendingUp className="w-5 h-5 text-orange-600" />}
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                              <h5 className="font-medium text-gray-900">{event.eventName}</h5>
                                {(event as any).isWinner && (
                                  <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded-full text-xs font-bold">
                                    당첨
                                  </span>
                                )}
                                {(event as any).rank && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    {((event as any).rank)}위
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{event.requirements}</p>
                              <p className="text-xs text-gray-500">참여일: {formatDate(event.participatedAt)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              event.status === 'completed' ? 'bg-green-100 text-green-800' :
                              event.status === 'active' ? 'bg-blue-100 text-blue-800' :
                              (event.status as string) === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {event.status === 'completed' ? '완료' :
                               event.status === 'active' ? '진행중' :
                               (event.status as string) === 'pending' ? '대기중' : 
                               (event.status as string) === 'expired' ? '만료' : String(event.status)}
                            </span>
                            {event.reward > 0 && (
                              <p className="text-sm font-semibold text-green-600 mt-1">
                                +{event.reward.toLocaleString()} VTTN
                              </p>
                            )}
                            {(event as any).prizeAmount > 0 && (event as any).prizeAmount !== event.reward && (
                              <p className="text-xs font-semibold text-yellow-600 mt-1">
                                당첨금: {(event as any).prizeAmount.toLocaleString()} VTTN
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {eventParticipations.length === 0 && (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">참여한 이벤트가 없습니다.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 인증 관리 탭 */}
          {activeTab === 'auth' && (
            <div className="space-y-6">
              {/* KYC 상태 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Shield className="w-5 h-5" />
                  <span>KYC 인증 상태</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">인증 상태</p>
                        <p className={`text-xl font-bold ${
                          selectedUser.kycStatus === 'verified' ? 'text-green-900' :
                          selectedUser.kycStatus === 'pending' ? 'text-yellow-900' :
                          'text-red-900'
                        }`}>
                          {selectedUser.kycStatus === 'verified' ? '인증 완료' :
                           selectedUser.kycStatus === 'pending' ? '검토 중' : '인증 거부'}
                        </p>
                      </div>
                      {selectedUser.kycStatus === 'verified' ? 
                        <CheckCircle className="w-8 h-8 text-green-600" /> :
                        selectedUser.kycStatus === 'pending' ?
                        <Clock className="w-8 h-8 text-yellow-600" /> :
                        <XCircle className="w-8 h-8 text-red-600" />
                      }
                    </div>
                  </div>
                  
                  {selectedUser.email && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">이메일 인증</p>
                          <p className="text-xl font-bold text-green-900">{selectedUser.email ? '완료' : '미인증'}</p>
                      </div>
                        {selectedUser.email ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                        ) : (
                          <XCircle className="w-8 h-8 text-gray-400" />
                        )}
                    </div>
                  </div>
                  )}
                  
                  {selectedUser.phone && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">휴대폰 인증</p>
                          <p className="text-xl font-bold text-purple-900">{selectedUser.phone ? '완료' : '미인증'}</p>
                      </div>
                        {selectedUser.phone ? (
                      <CheckCircle className="w-8 h-8 text-purple-600" />
                        ) : (
                          <XCircle className="w-8 h-8 text-gray-400" />
                        )}
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* 보안 설정 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Lock className="w-5 h-5" />
                  <span>보안 설정</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">2단계 인증 (2FA)</p>
                        <p className="text-sm text-gray-600">Google Authenticator 또는 SMS</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                      미구현
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Lock className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-gray-900">생체 인증</p>
                        <p className="text-sm text-gray-600">지문 또는 얼굴 인식</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedUser.biometricEnabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedUser.biometricEnabled ? '활성화' : '비활성화'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="font-medium text-gray-900">로그인 알림</p>
                        <p className="text-sm text-gray-600">새로운 기기에서 로그인 시 알림</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                      미구현
                    </span>
                  </div>
                </div>
              </div>

              {/* 로그인 기록 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Activity className="w-5 h-5" />
                  <span>최근 로그인 기록</span>
                </h3>
                <div className="space-y-3">
                  {selectedUser.lastActive ? (
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Activity className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">최근 활동</p>
                          <p className="text-sm text-gray-600">IP: {selectedUser.ipAddress || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-900">{formatTimeAgo(selectedUser.lastActive)}</p>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          활성
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">로그인 기록이 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 결제 내역 탭 */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              {/* 결제 통계 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">총 거래량</p>
                      <p className="text-xl font-bold text-blue-900">{selectedUser.totalVolume.toLocaleString()} VTTN</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">성공 거래</p>
                      <p className="text-xl font-bold text-green-900">
                        {userTransactions.filter(tx => tx.status === 'completed').length}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 font-medium">실패 거래</p>
                      <p className="text-xl font-bold text-red-900">
                        {userTransactions.filter(tx => tx.status === 'failed').length}
                      </p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">대기 중</p>
                      <p className="text-xl font-bold text-purple-900">
                        {userTransactions.filter(tx => tx.status === 'pending').length}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>

              {/* 결제 내역 - 실제 트랜잭션 데이터 사용 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>거래 내역 (결제/입출금)</span>
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  {userTransactions.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TX Hash</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">유형</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">입금/출금</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">금액</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">토큰</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {userTransactions.map((tx) => (
                          <tr key={tx.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">
                              {tx.hash ? `${tx.hash.slice(0, 10)}...${tx.hash.slice(-8)}` : tx.id}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{tx.type}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                tx.isDeposit ? 'bg-green-100 text-green-800' :
                                tx.isWithdrawal ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {tx.isDeposit ? '입금' : tx.isWithdrawal ? '출금' : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-sm font-semibold ${
                                tx.isDeposit ? 'text-green-600' :
                                tx.isWithdrawal ? 'text-red-600' :
                                'text-gray-900'
                              }`}>
                                {tx.isDeposit ? '+' : tx.isWithdrawal ? '-' : ''}
                                {tx.amount.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{tx.token}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                                tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                                {tx.status === 'completed' ? '완료' :
                                 tx.status === 'pending' ? '대기' : '실패'}
                            </span>
                          </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(tx.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  ) : (
                    <div className="text-center py-8">
                      <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">거래 내역이 없습니다.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 에어드롭 탭 */}
          {activeTab === 'airdrops' && (
            <div className="space-y-6">
              {/* 에어드롭 통계 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">웰컴 에어드롭</p>
                      <p className="text-xl font-bold text-green-900">
                        {selectedUser.welcomeAirdropReceived ? '수령 완료' : '미수령'}
                      </p>
                      <p className="text-xs text-green-600">
                        {eventParticipations
                          .filter(e => e.eventType === 'airdrop' && e.eventName?.includes('웰컴'))
                          .reduce((sum, e) => sum + e.reward, 0) || 30} VTTN
                      </p>
                    </div>
                    <Gift className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">총 에어드롭</p>
                      <p className="text-xl font-bold text-blue-900">{totalAirdropReceived.toLocaleString()}</p>
                      <p className="text-xs text-blue-600">VTTN</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">참여 이벤트</p>
                      <p className="text-xl font-bold text-purple-900">
                        {eventParticipations.filter(e => e.eventType === 'airdrop').length}
                      </p>
                      <p className="text-xs text-purple-600">개</p>
                    </div>
                    <Calendar className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
                
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-600 font-medium">대기 중</p>
                      <p className="text-xl font-bold text-orange-900">
                        {eventParticipations.filter(e => e.eventType === 'airdrop' && e.status === 'active').length}
                      </p>
                      <p className="text-xs text-orange-600">개</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* 에어드롭 내역 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                    <Gift className="w-4 h-4" />
                    <span>에어드롭 수령 내역</span>
                  </h4>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {/* 웰컴 에어드롭 */}
                    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Gift className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900">웰컴 보너스 에어드롭</h5>
                            <p className="text-sm text-gray-600">신규 가입 축하 보너스</p>
                            <p className="text-xs text-gray-500">수령일: {formatDate(selectedUser.joinedAt)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            selectedUser.welcomeAirdropReceived 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {selectedUser.welcomeAirdropReceived ? '수령 완료' : '대기 중'}
                          </span>
                          <p className="text-sm font-semibold text-green-600 mt-1">
                            +{eventParticipations
                              .filter(e => e.eventType === 'airdrop' && e.eventName?.includes('웰컴'))
                              .reduce((sum, e) => sum + e.reward, 0) || 30} VTTN
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 기타 에어드롭 */}
                    {eventParticipations
                      .filter(event => event.eventType === 'airdrop')
                      .map((event) => (
                        <div key={event.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <Gift className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <h5 className="font-medium text-gray-900">{event.eventName}</h5>
                                <p className="text-sm text-gray-600">{event.requirements}</p>
                                <p className="text-xs text-gray-500">참여일: {formatDate(event.participatedAt)}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                event.status === 'completed' ? 'bg-green-100 text-green-800' :
                                event.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {event.status === 'completed' ? '수령 완료' :
                                 event.status === 'active' ? '진행 중' : '종료'}
                              </span>
                              {event.reward > 0 && (
                                <p className="text-sm font-semibold text-green-600 mt-1">
                                  +{event.reward.toLocaleString()} VTTN
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    
                    {eventParticipations.filter(e => e.eventType === 'airdrop').length === 0 && (
                      <div className="text-center py-8">
                        <Gift className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">참여한 에어드롭이 없습니다.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;